import type { SignedInDto } from '@fyr/shared';
import { api } from './api';

/**
 * #147 M20.1 passkeys in the browser. The WebAuthn helper library loads only when someone uses one (not on first load).
 */
export const passkeysSupported = () =>
  typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';

/** "Sign in with a passkey": the browser offers this device's (or a nearby phone's) passkeys for Fyr. */
export async function signInWithPasskey(): Promise<SignedInDto> {
  const { startAuthentication } = await import('@simplewebauthn/browser');
  const optionsJSON =
    await api.post<Parameters<typeof startAuthentication>[0]['optionsJSON']>('/auth/passkey/options');
  const response = await startAuthentication({ optionsJSON });
  return api.post<SignedInDto>('/auth/passkey/verify', response);
}

/** A sensible name for a new passkey: the device and browser it was made on (the person can rename it later). */
export function deviceName() {
  const ua = navigator.userAgent;
  const device = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac OS X/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Linux/.test(ua)
              ? 'Linux'
              : 'Device';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : '';
  return browser ? `${device} · ${browser}` : device;
}

/** Add a passkey for the signed-in person. */
export async function addPasskey(name = deviceName()) {
  const { startRegistration } = await import('@simplewebauthn/browser');
  const optionsJSON =
    await api.post<Parameters<typeof startRegistration>[0]['optionsJSON']>('/auth/passkeys/options');
  const response = await startRegistration({ optionsJSON });
  return api.post<{ id: string }>('/auth/passkeys', { response, name });
}

/** The browser's "cancelled / timed out" (the person closed the prompt): not worth an error message. */
export const isCancelled = (e: unknown) =>
  e instanceof Error && (e.name === 'NotAllowedError' || e.name === 'AbortError');
